import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { filter, find, remove } from 'lodash-es';
import { TablesTranslations } from '~shared/components/tables/tables-translations.service';
import { TooltipTranslationService } from '~shared/translations';
import { Address, Attribute, Country, Customer } from '~core/models';
import {MergeCustomerService} from "~features/customer-merger/merge-customer.service";

/**
 * Displays the customer merge process page.
 */
@Component({
  selector: 'app-merge-customer-final',
  templateUrl: 'merge-customer-final.component.html',
  styleUrls: ['../../merge-customer.component.scss']
})
export class MergeCustomerFinalComponent {
  @Input() public countries: Array<Country>;
  @Input() public customer: Customer | any;
  @Input() public type: string;
  @Input() public triggered: boolean;
  @Output() public combinedCustomerUpdated: EventEmitter<any> = new EventEmitter(null);
  public i18nRequiredText: string = $localize`:@@mergeCustomer.text.required:required`;

  constructor(
    public tooltipTranslations: TooltipTranslationService,
    public tablesTranslations: TablesTranslations,
    public customerMergeService: MergeCustomerService,
  ) {
  }

  /**
   * Drag and drop event handler
   */
  onDrop(event: CdkDragDrop<any>): void {
    const data = event.item.data;
    const instance = this.checkInstance(data);
    if (instance) {
      this.checkData(data, instance);
    } else {
      this.combinedCustomerUpdated.emit(data);
    }
  }

  /**
   * Returns the age of the user
   */
  public getAge(birthday: any): any {
    const today = new Date();
    const birthDate = new Date(birthday);
    const m = today.getMonth() - birthDate.getMonth();
    let age = today.getFullYear() - birthDate.getFullYear();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  }

  /**
   * Checks if id already exists
   */
  public checkIfExists(data: any, instance: string): boolean {
    return find(this.customer[instance], { id: data.id });
  }

  /**
   * Checks if id already exists
   */
  public checkInstance(data: any): string | null {
    if (data instanceof Attribute) {
      return 'attributes';
    }
    if (data instanceof Address) {
      return 'addresses';
    }
    return null;
  }

  /**
   * Checks if id already exists
   */
  public checkData(data: any, instance: string): any {
    if (!this.checkIfExists(data, instance)) {
      if (instance === 'addresses' && this.checkStandardAddress(data)) {
        remove(this.customer[instance], el => el.type === 'standard');
      }
      this.customer[instance].push(data);
      this.combinedCustomerUpdated.emit(this.customer);
    }
  }

  /**
   * Removes the item from array
   */
  public removeItem(data: any): void {
    const instance = this.checkInstance(data);
    remove(this.customer[instance], el => el.id === data.id);
    this.combinedCustomerUpdated.emit(this.customer);
  }

  /**
   * Checks if the address is standard
   */
  private checkStandardAddress(data: Address): boolean {
    return data.type === 'standard' ? true : false;
  }

  /**
   * Retrieves the addresses
   */
  public getAddresses(type?: string): Array<any> {
    if (type) {
      return filter(this.customer.addresses, el => el.type === type);
    } else {
      return filter(this.customer.addresses, el => el.type !== 'standard');
    }
  }

  public checkIfRequired(field: string) {
    if (this.customerMergeService.requiredKeys.includes(field)) {
      if (field === 'addresses') {
        return !this.customer[field].length && this.customerMergeService.checkIfAnyCustomerHasValue(field);
      } else {
        return !this.customer[field] && this.customerMergeService.checkIfAnyCustomerHasValue(field);
      }
    } else {
      false;
    }
  }
}
